import { Roles } from '@common/types/user';
import { GenericDialog, GenericSuccessDialog } from '@components/display/dialogs';
import { LoadingContainer } from '@components/display/loading/LoadingContainer';
import { Tabs } from '@components/utility/tabs';
import { EventAnalyticsAccordion } from '@event/components/display/accordions';
import { EventBanner } from '@event/components/display/banners';
import { EventCancelDeleteDialog } from '@event/components/display/dialogs';
import { Badges } from '@event/components/display/tabs/Badges';
import { EventDetailEditActionBar, EventDetailViewActionBar } from '@event/components/layout';
import { EventDashboardContext, EventDashboardContextType } from '@event/contexts/EventContext';
import { useEvent } from '@event/hooks/useEvent';
import { EventOverview } from '@event/routes/dashboard/EventDetail';
import { EventEditSchema } from '@event/schemas/EventSchema';
import {
    useDeleteEventMutation,
    useGetEventCohostPermissionsQuery,
    useGetEventRequiredBadgesQuery,
    useGetEventRewardedBadgesQuery,
    useGetEventStatisticsQuery,
    usePatchEventPublishMutation,
    useSendEmailMutation,
    useUpdateEventMutation,
    useUploadEventBannerImageMutation,
} from '@event/services/events';
import {
    AttendeesTabs,
    EventDetailTabs,
    EventFormikType,
    EventTypes,
    KeyOfEvent,
    ManagementAnyEvent,
    ManagementOnSiteEvent,
    ManagementWebinarEvent,
    SignInGuestDialogState,
} from '@event/types/event';
import { withEventTypeCheck } from '@event/utils';
import { getEventRequiredBadges, getEventTypeTitle } from '@event/utils/event';
import { useAppDispatch } from '@hooks/generic';
import { useAuth } from '@hooks/useAuth';
import { usePrefixedNavigate } from '@hooks/usePrefixedNavigate';
import { useScrollIntoError } from '@hooks/useScrollIntoError';
import ArrowBack from '@mui/icons-material/ArrowBack';
import Check from '@mui/icons-material/Check';
import Edit from '@mui/icons-material/Edit';
import DraftsIcon from '@mui/icons-material/Drafts';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { Box, CircularProgress, Tabs as MUITabs, Typography } from '@mui/material';
import { CalendarIcon } from '@mui/x-date-pickers-pro';
import { api } from '@services/api';
import { setTitle } from '@store/appSlice';
import { handleError, nullEmptyStrings, toaster } from '@utils/common';
import { stripColorStyles } from '@utils/richTextEditor';
import { parseISO } from 'date-fns';
import { FormikProps, FormikProvider, useFormik } from 'formik';
import pluralize from 'pluralize';
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { NotFound } from 'src/routes/NotFound';

const EventAnalyticsOverview = lazy(
    () => import('@event/components/display/tabs/AnalyticsOverview/EventAnalyticsOverview')
);
const Cohost = lazy(() => import('@event/components/display/tabs/Cohost/Cohost'));
const Details = lazy(() => import('@event/components/display/tabs/Details/Details'));
const RegistrationDetails = lazy(
    () => import('@event/components/display/tabs/RegistrationDetails/RegistrationDetails')
);
const Audience = lazy(() => import('@event/components/display/tabs/Audience/Audience'));

const details_fields: KeyOfEvent[] = [
    'name',
    'date_start',
    'date_end',
    'time_zone',
    'description',
    'country',
    'location',
    'room_url',
    'dropbox_link',
];
const registration_details_fields: KeyOfEvent[] = [
    'questionnaire_id',
    'max_capacity',
    'registration_url',
    'possible_subscribers',
    'enable_followup',
    'notify_admins_after_feedback',
];
const audience_fields: KeyOfEvent[] = [
    'sync_to_mailchimp',
    'invitation_email_type',
    'notify_admins_after_registration',
];

const EventDetail = withEventTypeCheck(() => {
    // #region State
    const [accordionsState, setAccordionsState] = useState({
        analytics: true,
        cohost: true,
        details: true,
        feedback: true,
        registration_details: true,
        badges: true,
        audience: true,
    });
    const [activeAudienceFilters, setActiveAudienceFilters] = useState<Record<string, string[]>>({});
    const [sendUninvitedModalOpen, setSendUninvitedModalOpen] = useState(false);
    const [publishDialogOpen, setPublishDialogOpen] = useState(false);
    const [unpublishDialogOpen, setUnpublishDialogOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [successDialogOpen, setSuccessDialogOpen] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    const { type, eventId } = useParams();
    const eventType = type as EventTypes;
    const [tab, setTab] = useState<EventDetailTabs>(EventDetailTabs.OVERVIEW);
    const [attendeesTableTab, setAttendeesTableTab] = useState<AttendeesTabs>(0);
    const [signInGuestDialogState, setSignInGuestDialogState] = useState<SignInGuestDialogState>(false);

    const { event, isEventLoading, eventError, isEventFetching } = useEvent(eventType, Number(eventId));
    const [updateEvent, { isLoading: isEditLoading }] = useUpdateEventMutation();
    const [patchPublish, { isLoading: isPublishEventLoading }] = usePatchEventPublishMutation();
    const [deleteEvent, { isLoading: isDeleteLoading }] = useDeleteEventMutation();
    const [sendEmail, { isLoading: isSendEmailLoading }] = useSendEmailMutation();
    const dispatch = useAppDispatch();

    const { user } = useAuth();
    const [bannerPreview, setBannerPreview] = useState<string | null>(null);

    const [dialogText, setDialogText] = useState('');
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const navigate = usePrefixedNavigate();
    const intl = useIntl();
    // #endregion

    // #region Data

    const {
        data: statistics,
        isFetching: isStatisticsFetching,
        isLoading: isStatisticsLoading,
    } = useGetEventStatisticsQuery(
        {
            id: Number(eventId),
            type: eventType,
        },
        {
            /* pollingInterval: 5000, */
        }
    );
    const { data: cohostPermissions, isFetching: isCohostPermissionsLoading } = useGetEventCohostPermissionsQuery({
        id: Number(eventId),
        type: eventType,
    });

    const { data: requiredBadgesData, isLoading: isRequiredBadgesLoading } = useGetEventRequiredBadgesQuery({
        type: eventType,
        id: Number(eventId),
    });

    const { data: rewardedBadges, isLoading: isRewardedBadgesLoading } = useGetEventRewardedBadgesQuery({
        type: eventType,
        id: Number(eventId),
    });
    // #endregion

    // #region Logic & Handlers

    const requiredBadges = useMemo(
        () => (requiredBadgesData ? getEventRequiredBadges(requiredBadgesData) : []),
        [requiredBadgesData]
    );

    const isCohost = useMemo(() => {
        return event && user && (event.organizers ?? []).map((o) => o.user.id).includes(user.id);
    }, [event, user]);
    const isOrganizer = useMemo(() => event && user && event.owner?.id === user.id, [event, user]);
    const isUserSalesOrAdmin = useMemo(
        () => user && [Roles.VICTRON, Roles.SALES_DIRECTOR, Roles.SALES_MANAGER].includes(user?.role),
        [user]
    );
    const isAdmin = useMemo(() => user && user.role === Roles.VICTRON, [user]);
    const isOwnerOrAnyOrganizersNotSalesOrAdmin = useMemo(
        () =>
            event &&
            [
                ...(event.owner?.role ? [event.owner.role] : []),
                ...(event.organizers ?? []).map((o) => o.user.role),
            ].some((r) => ![Roles.SALES_DIRECTOR, Roles.SALES_MANAGER, Roles.VICTRON].includes(r)),
        [event]
    );

    const [uploadBannerImage, { isLoading: uploadingBannerImage }] = useUploadEventBannerImageMutation();

    const handleSendEmail = async () => {
        try {
            await sendEmail({
                type: eventType,
                id: Number(eventId),
            }).unwrap();
            toaster.success({ msg: 'Successfully sent emails' });
            setSendUninvitedModalOpen(false);
        } catch (err) {
            handleError('Error sending emails', err);
        }
    };

    const validateFormAndScroll = async (formik: FormikProps<EventFormikType>, updatedValues?: EventFormikType) => {
        let errors;

        try {
            await EventEditSchema.validate(updatedValues ?? formik.values, { abortEarly: false });
            errors = {};
        } catch (err: any) {
            errors = err.inner.reduce((acc: any, error: any) => {
                acc[error.path] = error.message;
                return acc;
            }, {});
        }

        formik.setErrors(errors);

        if (Object.keys(errors).length > 0) {
            setTab(EventDetailTabs.OVERVIEW);
            const touchedFields = Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), {});
            formik.setTouched(touchedFields);

            const errorFields = Object.keys(errors) as KeyOfEvent[];
            setAccordionsState((prevState) => ({
                ...prevState,
                details: errorFields.some((ef) => details_fields.includes(ef)) ? true : prevState.details,
                registration_details: errorFields.some((ef) => registration_details_fields.includes(ef))
                    ? true
                    : prevState.registration_details,
                audience: errorFields.some((ef) => audience_fields.includes(ef)) ? true : prevState.audience,
            }));
            scrollIntoError();

            return { isValid: false, errors };
        }

        return { isValid: true, errors: {} };
    };

    const saveEvent = async (values: EventFormikType) => {
        if (values.banner)
            await uploadBannerImage({
                id: event?.id,
                type: eventType,
                banner_image: values.banner[0].file,
            });

        const newEvent: Partial<
            Omit<ManagementAnyEvent, 'possible_subscribers'> & {
                possible_subscribers?: string;
            }
        > = {
            ...values,
            possible_subscribers: values.possible_subscribers?.join(',') ?? '',
            enable_questionnaire: values.questionnaire_id?.trim() !== '' ? !!values.enable_questionnaire : false,
        };
        delete newEvent.owner;

        const finalPayload = {
            ...nullEmptyStrings(newEvent),
            registration_url: values.registration_url ?? '',
            city: values.city ?? '',
            room_url: values.room_url ?? '',
        };

        await updateEvent(finalPayload).unwrap();
    };

    const [isEdit, setIsEdit] = useState(false);
    const [committedPublishedState, setCommittedPublishedState] = useState<boolean>();

    // Memoize initial values, using committed published state when available
    // in order to track published state changes made outside the form.
    const initialValues = useMemo(() => {
        const getInitialValues = (newEvent?: ManagementOnSiteEvent | ManagementWebinarEvent) => ({
            ...newEvent,
            description: stripColorStyles(newEvent?.description ?? ''),
            organizers_user_ids: newEvent?.organizers?.map((organizer) => organizer.user.id) ?? [],
            type: eventType,
            attendees: statistics?.subscribers_accepted_count ?? 0,
            city: newEvent?.city ?? '',
        });

        const baseValues = getInitialValues(event);
        if (committedPublishedState !== undefined) {
            return { ...baseValues, published: committedPublishedState };
        }
        return baseValues;
    }, [event, committedPublishedState, eventType, statistics]);

    const formik = useFormik<EventFormikType>({
        initialValues: initialValues,
        validationSchema: EventEditSchema,
        onSubmit: async (values, helpers) => {
            try {
                setIsSaving(true);
                await saveEvent(values);
                dispatch(api.util.invalidateTags([{ type: 'Events', id: eventType + '-' + eventId }]));
                reset(false);
                helpers.setFieldValue('banner', null);
                setIsSaving(false);
                if (
                    (isOrganizer ||
                        (isCohost && !!cohostPermissions?.find((perm) => perm.role === user?.role)?.send_invites)) &&
                    values.invitation_email_type === 'auto' &&
                    Number(statistics?.uninvited_subscribers) > 0 &&
                    values.date_end &&
                    new Date() < parseISO(values.date_end) &&
                    values?.published
                ) {
                    setSendUninvitedModalOpen(true);
                }
            } catch (err) {
                handleError('Update event failed', err);
            } finally {
                setIsSaving(false);
            }
        },
        enableReinitialize: true,
    });

    const { scrollIntoError } = useScrollIntoError({ formik });

    const { errors, handleSubmit } = formik;

    const getErrorCountForTab = useCallback(
        (tabFields: KeyOfEvent[]) => {
            const errorFields = Object.keys(errors) as KeyOfEvent[];
            return errorFields.filter((errorField) => tabFields.includes(errorField)).length;
        },
        [errors]
    );

    const errorCounts = useMemo(
        () => ({
            details: getErrorCountForTab(details_fields),
            registration: getErrorCountForTab(registration_details_fields),
            audience: getErrorCountForTab(audience_fields),
        }),
        [getErrorCountForTab]
    );

    const contextValue: EventDashboardContextType = useMemo(
        () => ({
            user,
            cohostPermissions,
            event,
            isEdit,
            isCohost,
            isOrganizer,
            isUserSalesOrAdmin,
            isAdmin,
            isOwnerOrAnyOrganizersNotSalesOrAdmin,
            committedPublishedState,
            statistics: { data: statistics, isFetching: isStatisticsFetching, isLoading: isStatisticsLoading },
            badges: {
                required: { data: requiredBadges, isLoading: isRequiredBadgesLoading },
                rewarded: { data: rewardedBadges, isLoading: isRewardedBadgesLoading },
            },
            accordionsState: [accordionsState, setAccordionsState],
            attendeesTabState: [attendeesTableTab, setAttendeesTableTab],
            activeAudienceFiltersState: [activeAudienceFilters, setActiveAudienceFilters],
            signInGuestDialogState: [signInGuestDialogState, setSignInGuestDialogState],
            isEventFetching,
            tabState: [tab, setTab],
        }),
        [
            user,
            cohostPermissions,
            event,
            isEdit,
            isCohost,
            isOrganizer,
            isUserSalesOrAdmin,
            isAdmin,
            isOwnerOrAnyOrganizersNotSalesOrAdmin,
            committedPublishedState,
            statistics,
            isStatisticsFetching,
            accordionsState,
            attendeesTableTab,
            activeAudienceFilters,
            signInGuestDialogState,
            isEventFetching,
            tab,
        ]
    );

    const reset = useCallback(
        (resetValues = true) => {
            setIsEdit(false);
            formik.setFieldValue('banner', undefined);
            setBannerPreview(null);
            if (resetValues) {
                formik.setValues(initialValues);
            }
            formik.setErrors({});
            setIsDialogOpen(false);
            setDialogText('cancel');
        },
        [formik, setBannerPreview, initialValues]
    );

    const handleRemove = async () => {
        try {
            if (event?.id) {
                await deleteEvent({ id: event?.id, type: eventType });
                setIsDialogOpen(false);
                navigate('/dashboard/events');
                toaster.success({ msg: 'Event successfully removed' });
            } else {
                throw 'Invalid event to remove';
            }
        } catch (err) {
            handleError('Error removing event', err);
        }
    };

    const handleDialogOpen = (dialogMode: string) => {
        setDialogText(dialogMode);
        setIsDialogOpen(true);
    };

    const handleDialogClose = () => {
        setIsDialogOpen(false);
    };

    const handleFormSubmit = useCallback(
        async (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            e.stopPropagation();

            const validation = await validateFormAndScroll(formik);

            if (validation.isValid) {
                return handleSubmit(e);
            }
        },
        [formik, handleSubmit]
    );

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLFormElement>) => {
        if (e.key === 'Enter') {
            if ((e.target as HTMLElement).tagName.toLowerCase() === 'textarea') {
                return;
            }

            e.preventDefault();
            e.stopPropagation();
        }
    }, []);

    const CurrentTab = useMemo(() => {
        switch (tab) {
            case EventDetailTabs.ANALYTICS:
                return <EventAnalyticsOverview />;
            case EventDetailTabs.COHOST:
                return <Cohost isLoading={isCohostPermissionsLoading} accordion={false} />;
            case EventDetailTabs.DETAILS:
                return <Details accordion={false} />;
            case EventDetailTabs.REGISTRATION:
                return <RegistrationDetails accordion={false} />;
            case EventDetailTabs.BADGES:
                return <Badges accordion={false} />;
            case EventDetailTabs.AUDIENCE:
                return <Audience accordion={false} />;
            case EventDetailTabs.OVERVIEW:
            default:
                return <EventOverview isCohostPermissionsLoading={isCohostPermissionsLoading} />;
        }
    }, [tab, isCohostPermissionsLoading]);

    const handlePublishClick = async () => {
        formik.setFieldValue('published', true);

        await validateFormAndScroll(formik);
        setPublishDialogOpen(true);
    };

    const handleUnpublishClick = () => {
        setUnpublishDialogOpen(true);
    };

    const handlePublishConfirm = async () => {
        const updatedValues = { ...formik.values, published: true };
        const validation = await validateFormAndScroll(formik, updatedValues);

        if (!validation.isValid) {
            setPublishDialogOpen(false);
            setIsEdit(true);
        } else {
            try {
                await patchPublish({
                    id: Number(eventId),
                    type: eventType,
                    published: true,
                }).unwrap();

                setPublishDialogOpen(false);
                setCommittedPublishedState(true);
                dispatch(api.util.invalidateTags([{ type: 'Events', id: eventType + '-' + eventId }]));
                setSuccessMessage('Your event is now successfully published.');
                setSuccessDialogOpen(true);
            } catch (err) {
                handleError('Publish failed', err);
                formik.setFieldValue('published', false);
            }
        }
    };

    const handlePublishCancel = async () => {
        setPublishDialogOpen(false);
        formik.setFieldValue('published', event?.published ?? false);
        formik.setErrors({});
    };

    const handleUnpublishConfirm = async () => {
        try {
            await patchPublish({
                id: Number(eventId),
                type: eventType,
                published: false,
            }).unwrap();

            setUnpublishDialogOpen(false);
            setCommittedPublishedState(false);
            dispatch(api.util.invalidateTags([{ type: 'Events', id: eventType + '-' + eventId }]));
            setSuccessMessage('The event has been successfully unpublished.');
            setSuccessDialogOpen(true);
        } catch (err) {
            handleError('Unpublish failed', err);
            formik.setFieldValue('published', true);
        }
    };

    const handleUnpublishCancel = () => {
        setUnpublishDialogOpen(false);
        formik.setFieldValue('published', true);
    };

    const handleSaveClick = useCallback(
        async (actualPublishedState?: boolean) => {
            try {
                const updatedValues = { ...formik.values, published: actualPublishedState ?? committedPublishedState };

                formik.setFieldValue('published', actualPublishedState ?? committedPublishedState);
                const validation = await validateFormAndScroll(formik, updatedValues);

                if (!validation.isValid) {
                    return;
                } else {
                    await formik.submitForm();
                    setSuccessMessage('All changes have been saved successfully.');
                    setSuccessDialogOpen(true);
                }
            } catch (err) {
                handleError('Save failed', err);
            }
        },
        [formik.values, committedPublishedState]
    );

    const handleSaveAndPublishClick = async () => {
        try {
            const updatedValues = { ...formik.values, published: true };
            formik.setFieldValue('published', true);

            const validation = await validateFormAndScroll(formik, updatedValues);

            if (!validation.isValid) {
                return;
            }

            await formik.submitForm();

            await patchPublish({
                id: Number(eventId),
                type: eventType,
                published: true,
            }).unwrap();

            dispatch(api.util.invalidateTags([{ type: 'Events', id: eventType + '-' + eventId }]));

            setCommittedPublishedState(true);
            reset(false);
            formik.setFieldValue('banner', null);
            setSuccessMessage('All changes have been saved and your event is now successfully published.');
            setSuccessDialogOpen(true);
        } catch (err) {
            handleError('Save and publish failed', err);
            formik.setFieldValue('published', false);
        }
    };
    // #endregion

    // #region Effects
    useEffect(() => {
        const userNavbar = document.getElementById('user-navbar');
        userNavbar?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    useEffect(() => {
        if (committedPublishedState === undefined) setCommittedPublishedState(event?.published);
    }, [event]);

    useEffect(() => {
        dispatch(setTitle(getEventTypeTitle(intl, eventType) ?? ''));
    }, [dispatch, eventType, intl.locale]);
    // #endregion

    // #region Component

    return (
        <EventDashboardContext.Provider value={contextValue}>
            <LoadingContainer isLoading={isEventLoading || isSaving}>
                {!eventError ? (
                    <>
                        <form
                            className="flex flex-col gap-5"
                            onSubmit={handleFormSubmit}
                            onKeyDown={handleKeyDown}
                            autoComplete="off"
                        >
                            <FormikProvider value={formik}>
                                <div className="bg-white p-5 rounded-lg flex justify-between">
                                    {!isEdit ? (
                                        <EventDetailViewActionBar
                                            onEditClick={() => setIsEdit(true)}
                                            onPublishClick={handlePublishClick}
                                            onUnpublishClick={handleUnpublishClick}
                                            isSaving={isSaving}
                                        />
                                    ) : (
                                        <EventDetailEditActionBar
                                            onCancelClick={() => handleDialogOpen('cancel')}
                                            onDeleteClick={() => handleDialogOpen('remove this event')}
                                            onSaveClick={handleSaveClick}
                                            onSaveAndPublishClick={handleSaveAndPublishClick}
                                            isLoading={isEditLoading || uploadingBannerImage || isPublishEventLoading}
                                        />
                                    )}
                                </div>
                                <EventBanner
                                    {...{ isEdit, formik }}
                                    previewState={{ bannerPreview, setBannerPreview }}
                                />
                                <div className="bg-white p-5 rounded-lg">
                                    <EventAnalyticsAccordion
                                        isExpanded={accordionsState.analytics}
                                        handleAccordionChange={() =>
                                            setAccordionsState((prevState) => ({
                                                ...prevState,
                                                analytics: !prevState.analytics,
                                            }))
                                        }
                                    />
                                </div>
                                <div className="bg-white p-5 rounded-lg">
                                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                        <MUITabs
                                            className="gap-5"
                                            value={tab}
                                            onChange={(_, newValue) => setTab(newValue)}
                                            slotProps={{
                                                indicator: {
                                                    sx: {
                                                        display: 'block',
                                                        backgroundColor: (() => {
                                                            switch (tab) {
                                                                case EventDetailTabs.DETAILS:
                                                                    return errorCounts.details > 0
                                                                        ? '#EF4444'
                                                                        : '#1976d2';
                                                                case EventDetailTabs.REGISTRATION:
                                                                    return errorCounts.registration > 0
                                                                        ? '#EF4444'
                                                                        : '#1976d2';
                                                                default:
                                                                    return '#1976d2';
                                                            }
                                                        })(),
                                                    },
                                                },
                                            }}
                                        >
                                            <Tabs.Tab label="Analytics Overview" />
                                            <Tabs.Tab
                                                label={
                                                    <div className="flex items-center gap-2">
                                                        <span>Co-host</span>
                                                    </div>
                                                }
                                            />
                                            <Tabs.Tab
                                                label={
                                                    <div className="flex items-center gap-2">
                                                        <span>Details</span>
                                                        {errorCounts.details > 0 && (
                                                            <Typography
                                                                color="error-500.main"
                                                                fontWeight={500}
                                                                fontSize={14}
                                                            >
                                                                {errorCounts.details} Mandatory{' '}
                                                                {pluralize('field', errorCounts.details, false)} for
                                                                publishing
                                                            </Typography>
                                                        )}
                                                    </div>
                                                }
                                            />
                                            <Tabs.Tab
                                                label={
                                                    <div className="flex items-center gap-2">
                                                        <span>Registration Details</span>
                                                        {errorCounts.registration > 0 && (
                                                            <Typography
                                                                color="error-500.main"
                                                                fontWeight={500}
                                                                fontSize={14}
                                                            >
                                                                {errorCounts.registration} Mandatory{' '}
                                                                {pluralize('field', errorCounts.registration, false)}{' '}
                                                                for publishing
                                                            </Typography>
                                                        )}
                                                    </div>
                                                }
                                            />
                                            <Tabs.Tab
                                                label={
                                                    <div className="flex items-center gap-2">
                                                        <span>Event Badges</span>
                                                    </div>
                                                }
                                            />
                                            <Tabs.Tab
                                                label={
                                                    <div className="flex items-center gap-2">
                                                        <span>Audience</span>
                                                        {errorCounts.audience > 0 && (
                                                            <Typography
                                                                color="error-500.main"
                                                                fontWeight={500}
                                                                fontSize={14}
                                                            >
                                                                {errorCounts.audience} Mandatory
                                                                {pluralize('field', errorCounts.audience, false)} for
                                                                publishing
                                                            </Typography>
                                                        )}
                                                    </div>
                                                }
                                            />
                                            <Tabs.Tab label="Complete event overview" className="ml-auto" />
                                        </MUITabs>
                                    </Box>
                                    <div>
                                        <Suspense
                                            fallback={
                                                <Box minHeight={800} position="relative">
                                                    <CircularProgress className="absolute-center" />
                                                </Box>
                                            }
                                        >
                                            {CurrentTab}
                                        </Suspense>
                                    </div>
                                </div>
                                <EventCancelDeleteDialog
                                    open={!!isDialogOpen}
                                    onTransitionExited={() => setDialogText('')}
                                    title={`Are you sure you want to ${dialogText}?`}
                                    primary={
                                        dialogText === 'cancel'
                                            ? {
                                                  label: 'Cancel editing',
                                                  onClick: reset,
                                              }
                                            : {
                                                  label: 'Remove event',
                                                  onClick: handleRemove,
                                                  loading: isDeleteLoading,
                                              }
                                    }
                                    secondary={{
                                        label: 'Go back',
                                        onClick: handleDialogClose,
                                    }}
                                />
                            </FormikProvider>
                        </form>
                        <GenericDialog
                            open={publishDialogOpen}
                            disableEscapeKeyDown={false}
                            onClose={handlePublishCancel}
                            titleSX={
                                <div className="flex flex-col items-center text-center">
                                    <Typography
                                        fontFamily="museo-sans"
                                        fontSize={32}
                                        fontWeight={500}
                                        letterSpacing={-0.8}
                                        lineHeight="110%"
                                        className="mb-3"
                                    >
                                        {Object.keys(formik.errors).length > 0
                                            ? 'Fill in Missing Fields'
                                            : 'Are you sure you want to publish this event?'}
                                    </Typography>
                                    <Typography fontSize={16} sx={{ whiteSpace: 'pre-line' }}>
                                        {Object.keys(formik.errors).length > 0
                                            ? `You are unable to publish your event because some mandatory fields are missing.\nPlease complete all required fields and try publishing again.`
                                            : "Publishing will make the event live and visible to the public.\nYou can still make edits after publishing, but it's best to review all details one last time."}
                                    </Typography>
                                </div>
                            }
                            secondaryBtnProps={{
                                title: 'Go Back',
                                onClick: handlePublishCancel,
                                startIcon: <ArrowBack />,
                                variant: 'contained',
                            }}
                            primaryBtnProps={{
                                title: Object.keys(formik.errors).length > 0 ? 'Edit Missing Fields' : 'Publish',
                                onClick: handlePublishConfirm,
                                color: 'primary',
                                startIcon: isPublishEventLoading ? (
                                    <CircularProgress size={20} />
                                ) : Object.keys(errors).length > 0 ? (
                                    <Edit />
                                ) : (
                                    <CalendarIcon />
                                ),
                            }}
                        />
                        <GenericDialog
                            open={unpublishDialogOpen}
                            disableEscapeKeyDown={false}
                            onClose={handleUnpublishCancel}
                            titleSX={
                                <div className="flex flex-col items-center text-center">
                                    <Typography
                                        fontFamily="museo-sans"
                                        fontSize={32}
                                        fontWeight={500}
                                        letterSpacing={-0.8}
                                        lineHeight="110%"
                                        className="mb-3"
                                    >
                                        Unpublish Event
                                    </Typography>
                                    <Typography fontSize={16}>
                                        Are you sure you want to unpublish {event?.name}?
                                    </Typography>
                                </div>
                            }
                            secondaryBtnProps={{
                                title: 'Go Back',
                                onClick: handleUnpublishCancel,
                                startIcon: <ArrowBack />,
                                variant: 'contained',
                            }}
                            primaryBtnProps={{
                                title: 'Unpublish Event',
                                onClick: handleUnpublishConfirm,
                                color: 'primary',
                                startIcon: isPublishEventLoading ? <CircularProgress size={20} /> : <Check />,
                                disabled: isPublishEventLoading,
                            }}
                        />
                        <GenericDialog
                            open={sendUninvitedModalOpen}
                            classes={{
                                paper: 'w-[828px]',
                            }}
                            titleSX={
                                <div className="flex flex-col items-center">
                                    <div className="max-w-[400px]">
                                        <Typography
                                            fontFamily="museo-sans"
                                            fontSize={32}
                                            letterSpacing={-0.8}
                                            textAlign="center"
                                            lineHeight="110%"
                                            marginBottom={1}
                                        >
                                            Do you want to invite {statistics?.uninvited_subscribers} guests to your
                                            event?
                                        </Typography>
                                    </div>
                                    <Typography
                                        fontSize={16}
                                        letterSpacing={0}
                                        color="neutral-900.main"
                                        textAlign="center"
                                    >
                                        By clicking send invitations, your guest will get notified via email.
                                    </Typography>
                                </div>
                            }
                            primaryBtnProps={{
                                title: 'Send Invitations Now',
                                color: 'primary',
                                startIcon: isSendEmailLoading ? <CircularProgress size={20} /> : <MailOutlineIcon />,
                                disabled: isSendEmailLoading,
                                onClick: handleSendEmail,
                            }}
                            secondaryBtnProps={{
                                title: 'Send Invitations Later',
                                startIcon: <DraftsIcon />,
                                onClick: () => setSendUninvitedModalOpen(false),
                                disabled: isSendEmailLoading,
                            }}
                            onClose={() => setSendUninvitedModalOpen(false)}
                        />
                        <GenericSuccessDialog
                            open={successDialogOpen}
                            onClose={() => setSuccessDialogOpen(false)}
                            contentProps={{
                                description: successMessage,
                                btn: {
                                    text: 'Continue',
                                    onClick: () => setSuccessDialogOpen(false),
                                },
                            }}
                        />
                    </>
                ) : (
                    <NotFound />
                )}
            </LoadingContainer>
        </EventDashboardContext.Provider>
    );
});
// #endregion

export { EventDetail };
